import { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import CustomAlert from '../../components/CustomAlert';
import config from '../../config/Constant';
import { postRequest } from '../../helpers/apiHandler';
import CloseIcon from '../../layouts/SVG/CloseIcon';

const WhatsAppPricingRatesUploadModal = ({ show, onClose, getPricingRatesData }) => {
  const { access_token } = useSelector((state) => state.auth.user);
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const resetForm = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileName = selectedFile.name.toLowerCase();
      if (!fileName.endsWith('.csv') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        setAlertState({
          message: 'Please upload a CSV or Excel file',
          show: true,
          variant: 'danger',
        });
        e.target.value = ''; // Reset input
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!file) {
      setAlertState({
        message: 'Please select a file to upload',
        show: true,
        variant: 'danger',
      });
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append('file', file);

    postRequest('/v2/admin/whatsapp/pricing-rates/upload', formData, access_token.token, (res) => {
      setLoading(false);
      if (res.success) {
        setAlertState({
          message: res.message || 'Pricing rates uploaded successfully',
          show: true,
          variant: 'success',
        });
        setTimeout(() => {
          handleClose();
          getPricingRatesData();
        }, 1500);
      } else {
        setAlertState({
          message: res.message || 'Failed to upload pricing rates',
          show: true,
          variant: 'danger',
        });
      }
    });
  };

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({ message: '', show: false, variant: '' });
      }, config.notify_time);
    }
  }, [alertState]);

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Body style={{ padding: '24px' }}>
        <CloseIcon handleModalClose={handleClose} />
        <Modal.Title as="h4" style={{ margin: '8px 0 24px' }}>
          Upload Pricing Rates
        </Modal.Title>

        <CustomAlert
          message={alertState.message}
          show={alertState.show}
          variant={alertState.variant}
          closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
        />

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-4">
            <Form.Label>Select File (CSV/Excel)</Form.Label>
            <Form.Control
              type="file"
              onChange={handleFileChange}
              ref={fileInputRef}
              accept=".csv, .xlsx, .xls"
              required
            />
            <Form.Text className="text-muted">Supported formats: .csv, .xlsx, .xls</Form.Text>
            <div className="mt-2 text-small">
              <a
                href={process.env.PUBLIC_URL + '/sample_pricing_rates.csv'}
                download="sample_pricing_rates.csv"
                style={{ fontSize: '0.85rem' }}
              >
                Download Sample Template
              </a>
            </div>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                'Upload'
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default WhatsAppPricingRatesUploadModal;
